import html from "./results-table.html?raw"
import styles from "./results-table.css?inline"
import { sharedStyles } from "../../shared-styles"
import type { RDFTerm, SPARQLSelectResults } from "../sparql-view/types"
import { formatRDFTerm, escapeHtml, type PrefixMap } from "./formatters"
import { VirtualScroller } from "./virtual-scroller"

// Threshold for enabling virtual scrolling
const VIRTUAL_SCROLL_THRESHOLD = 100
const ROW_HEIGHT = 32 // pixels
const BUFFER_ROWS = 10

/** A single SELECT result row, keyed by variable name. */
export type RowBinding = Record<string, RDFTerm>

/**
 * A lazily-fetched, bounded source of SELECT result rows. Lets the table render
 * an arbitrarily large result set by pulling only the visible window via
 * `getRange` instead of materializing every binding in memory. Backed by a
 * graph-store `SelectCursor` in production (see data-view), but kept structural
 * so it stays trivially testable.
 */
export interface RowSource {
  total: number
  vars: string[]
  getRange(offset: number, count: number): Promise<RowBinding[]>
}

interface CachedWindow {
  offset: number
  rows: RowBinding[]
}

// How many fetched windows to retain. Bounds main-thread memory while scrolling
// through a large result set; older windows are evicted FIFO.
const MAX_CACHED_WINDOWS = 8

class ResultsTable extends HTMLElement {
  private shadow: ShadowRoot
  private _selectResults: SPARQLSelectResults | null = null
  private _rowSource: RowSource | null = null
  private _prefixes: PrefixMap = new Map()
  private virtualScroller: VirtualScroller | null = null
  private boundHandleScroll: () => void

  // Client-side cache of fetched windows (last MAX_CACHED_WINDOWS) plus the set
  // of window offsets with an in-flight getRange, to avoid duplicate fetches.
  private windowCache: CachedWindow[] = []
  private pendingWindows = new Set<number>()

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: "open" })
    this.shadow.adoptedStyleSheets = [sharedStyles]
    const template = document.createElement("template")
    template.innerHTML = `<style>${styles}</style>${html}`
    this.shadow.appendChild(template.content.cloneNode(true))
    this.boundHandleScroll = this.handleScroll.bind(this)
  }

  connectedCallback() {
    const container = this.shadow.getElementById("table-container")
    if (container) {
      container.addEventListener("scroll", this.boundHandleScroll)
    }
  }

  disconnectedCallback() {
    const container = this.shadow.getElementById("table-container")
    if (container) {
      container.removeEventListener("scroll", this.boundHandleScroll)
    }
  }

  set selectResults(results: SPARQLSelectResults | null) {
    this._selectResults = results
    this._rowSource = null
    this.virtualScroller = null
    this.clearWindowCache()
    this.render()
  }

  get selectResults(): SPARQLSelectResults | null {
    return this._selectResults
  }

  set rowSource(source: RowSource | null) {
    this._rowSource = source
    this._selectResults = null
    this.virtualScroller = null
    this.clearWindowCache()
    this.render()
  }

  get rowSource(): RowSource | null {
    return this._rowSource
  }

  set prefixes(map: PrefixMap) {
    this._prefixes = map
    this.render()
  }

  scrollToTop() {
    const container = this.shadow.getElementById("table-container")
    if (container) {
      container.scrollTop = 0
    }
  }

  private handleScroll() {
    if (this._rowSource && this.virtualScroller) {
      void this.renderRowSourceWindow()
    } else if (this.virtualScroller && this._selectResults) {
      this.renderVirtualRows()
    }
  }

  private render() {
    const thead = this.shadow.getElementById("table-head")!
    const tbody = this.shadow.getElementById("table-body")!

    if (this._rowSource) {
      this.renderRowSource(thead, tbody, this._rowSource)
      this.scrollToTop()
      return
    }

    if (!this._selectResults) {
      thead.innerHTML = ""
      tbody.innerHTML = ""
      return
    }

    const useVirtualScroll =
      this._selectResults.results.bindings.length > VIRTUAL_SCROLL_THRESHOLD
    if (useVirtualScroll) {
      this.renderVirtual(thead, tbody, this._selectResults)
    } else {
      this.renderAll(thead, tbody, this._selectResults)
    }

    this.scrollToTop()
  }

  private renderHead(thead: HTMLElement, vars: string[]) {
    thead.innerHTML = `<tr>${vars.map((v) => `<th>${escapeHtml(v)}</th>`).join("")}</tr>`
  }

  private renderRow(vars: string[], binding: RowBinding): string {
    const cells = vars.map((variable) => {
      const term = binding[variable]
      if (!term) return "<td></td>"

      return `<td>${formatRDFTerm(term, this._prefixes)}</td>`
    })
    return `<tr>${cells.join("")}</tr>`
  }

  private renderAll(
    thead: HTMLElement,
    tbody: HTMLElement,
    results: SPARQLSelectResults,
  ) {
    this.renderHead(thead, results.head.vars)

    const rows = results.results.bindings.map((binding) =>
      this.renderRow(results.head.vars, binding),
    )

    tbody.innerHTML = rows.join("")
    tbody.style.height = ""
    tbody.style.position = ""
  }

  private renderVirtual(
    thead: HTMLElement,
    tbody: HTMLElement,
    results: SPARQLSelectResults,
  ) {
    this.renderHead(thead, results.head.vars)

    const container = this.shadow.getElementById("table-container")!
    const viewportHeight = container.clientHeight

    this.virtualScroller = new VirtualScroller({
      totalRows: results.results.bindings.length,
      rowHeight: ROW_HEIGHT,
      viewportHeight,
      bufferRows: BUFFER_ROWS,
    })

    tbody.innerHTML = ""
    tbody.style.height = ""
    tbody.style.position = ""

    this.renderVirtualRows()
  }

  private renderVirtualRows() {
    if (!this.virtualScroller || !this._selectResults) {
      return
    }

    const container = this.shadow.getElementById("table-container")!
    const tbody = this.shadow.getElementById("table-body")!
    const scrollTop = container.scrollTop

    this.virtualScroller.updateConfig({
      viewportHeight: container.clientHeight,
    })

    const { startIndex, endIndex } =
      this.virtualScroller.getVisibleRange(scrollTop)
    const results = this._selectResults
    const rowHeight = this.virtualScroller.getRowHeight()
    const columnCount = results.head.vars.length

    const rows: string[] = []

    const topSpacerHeight = startIndex * rowHeight
    if (topSpacerHeight > 0) {
      rows.push(this.spacerRow(topSpacerHeight, columnCount))
    }

    for (let i = startIndex; i < endIndex; i++) {
      rows.push(this.renderRow(results.head.vars, results.results.bindings[i]))
    }

    const bottomSpacerHeight =
      (this.virtualScroller.getTotalRows() - endIndex) * rowHeight
    if (bottomSpacerHeight > 0) {
      rows.push(this.spacerRow(bottomSpacerHeight, columnCount))
    }

    tbody.innerHTML = rows.join("")
  }

  private renderRowSource(
    thead: HTMLElement,
    tbody: HTMLElement,
    source: RowSource,
  ) {
    this.renderHead(thead, source.vars)

    const container = this.shadow.getElementById("table-container")!
    this.virtualScroller = new VirtualScroller({
      totalRows: source.total,
      rowHeight: ROW_HEIGHT,
      viewportHeight: container.clientHeight,
      bufferRows: BUFFER_ROWS,
    })

    tbody.innerHTML = ""
    tbody.style.height = ""
    tbody.style.position = ""

    void this.renderRowSourceWindow()
  }

  /**
   * Paint the currently-visible window of a RowSource, then fetch it if it is
   * not already cached. Cached rows render immediately; missing rows render as
   * placeholders and are filled in once `getRange` resolves. The scrollable
   * height is reserved by spacer rows sized to `source.total`, so the scrollbar
   * reflects the whole result set regardless of which window is loaded.
   */
  private async renderRowSourceWindow() {
    const source = this._rowSource
    const scroller = this.virtualScroller
    if (!source || !scroller) return

    const container = this.shadow.getElementById("table-container")!
    const tbody = this.shadow.getElementById("table-body")!

    // A cursor's total grows as it is pulled, so re-read it every paint and
    // keep the scroller sized to the latest total; otherwise the visible range
    // stays clamped to the initial total and rows past it become unreachable.
    scroller.updateConfig({
      viewportHeight: container.clientHeight,
      totalRows: source.total,
    })
    const { startIndex, endIndex } = scroller.getVisibleRange(
      container.scrollTop,
    )
    const count = endIndex - startIndex

    this.paintWindow(tbody, source, scroller, startIndex, endIndex)

    if (count <= 0) return
    if (this.windowIsCached(startIndex, count)) return
    if (this.pendingWindows.has(startIndex)) return

    this.pendingWindows.add(startIndex)
    try {
      const rows = await source.getRange(startIndex, count)
      this.cacheWindow(startIndex, rows)
    } finally {
      this.pendingWindows.delete(startIndex)
    }

    // The source or scroller may have been replaced while awaiting; only repaint
    // if this fetch still belongs to the live view. Recompute the visible range
    // in case the user scrolled during the fetch.
    if (this._rowSource !== source || this.virtualScroller !== scroller) return
    scroller.updateConfig({ totalRows: source.total })
    const range = scroller.getVisibleRange(container.scrollTop)
    this.paintWindow(tbody, source, scroller, range.startIndex, range.endIndex)
  }

  private paintWindow(
    tbody: HTMLElement,
    source: RowSource,
    scroller: VirtualScroller,
    startIndex: number,
    endIndex: number,
  ) {
    const rowHeight = scroller.getRowHeight()
    const columnCount = source.vars.length
    const rows: string[] = []

    const topSpacerHeight = startIndex * rowHeight
    if (topSpacerHeight > 0) {
      rows.push(this.spacerRow(topSpacerHeight, columnCount))
    }

    for (let i = startIndex; i < endIndex; i++) {
      const binding = this.cachedRow(i)
      rows.push(
        binding
          ? this.renderRow(source.vars, binding)
          : this.placeholderRow(columnCount),
      )
    }

    const bottomSpacerHeight = (source.total - endIndex) * rowHeight
    if (bottomSpacerHeight > 0) {
      rows.push(this.spacerRow(bottomSpacerHeight, columnCount))
    }

    tbody.innerHTML = rows.join("")
  }

  private spacerRow(height: number, columnCount: number): string {
    return `<tr class="spacer-row"><td colspan="${columnCount}" style="height: ${height}px; padding: 0; border: none;"></td></tr>`
  }

  private placeholderRow(columnCount: number): string {
    const cells = Array.from(
      { length: columnCount },
      () =>
        `<td class="placeholder-cell"><span class="placeholder-bar"></span></td>`,
    ).join("")
    return `<tr class="placeholder-row" aria-hidden="true">${cells}</tr>`
  }

  private cachedRow(index: number): RowBinding | undefined {
    for (const window of this.windowCache) {
      if (
        index >= window.offset &&
        index < window.offset + window.rows.length
      ) {
        return window.rows[index - window.offset]
      }
    }
    return undefined
  }

  private windowIsCached(offset: number, count: number): boolean {
    for (let i = offset; i < offset + count; i++) {
      if (!this.cachedRow(i)) return false
    }
    return true
  }

  private cacheWindow(offset: number, rows: RowBinding[]) {
    this.windowCache = this.windowCache.filter((w) => w.offset !== offset)
    this.windowCache.push({ offset, rows })
    while (this.windowCache.length > MAX_CACHED_WINDOWS) {
      this.windowCache.shift()
    }
  }

  private clearWindowCache() {
    this.windowCache = []
    this.pendingWindows.clear()
  }
}

customElements.define("results-table", ResultsTable)
export { ResultsTable }
